// ============================================================
// TMR Portal Admin — como uma linha do histórico de taxa se lê
// ============================================================
import { formatRateValue } from '../rateFormat';
import type { RateChangeKind, RateChangeOrigin, RateHistoryEntry } from './contract';

/** What each recorded change is called on screen. */
export const CHANGE_KIND_LABEL: Record<RateChangeKind, string> = {
  Created: 'Taxa criada',
  Updated: 'Valor alterado',
  Deleted: 'Taxa excluída',
  TemporaryApplied: 'Taxa temporária aplicada',
  TemporaryEnded: 'Taxa temporária encerrada',
};

/**
 * Where the change came from, in the operator's words.
 *
 * These are the sentences that make the trail worth keeping: "the legacy decided this" and "somebody here
 * decided this" look identical in a table of numbers, and they call for opposite reactions.
 */
export const CHANGE_ORIGIN_LABEL: Record<RateChangeOrigin, string> = {
  Portal: 'pelo portal',
  LegacyReconciliation: 'trazido do sistema legado',
  TemporaryCascade: 'em cascata a partir da taxa da empresa',
  TemplatePropagation: 'propagado do molde da TMR',
};

/**
 * Who is credited with the change.
 *
 * A change with no operator is the normal case for anything the legacy or a worker did, and it says so
 * rather than leaving a blank that reads like missing data.
 *
 * @param entry The recorded change.
 */
export function actorLabel(entry: RateHistoryEntry): string {
  return entry.operatorName ?? 'Sem operador — mudança automática';
}

/**
 * How the values moved, as one phrase: `3,03% → 1,50%`.
 *
 * The temporary values are shown apart from the standard one because they answer different questions —
 * what the clinic pays today, and what it goes back to. An entry that folded them together would be unable
 * to say that ending a window changed what is charged without changing the rate.
 *
 * @param entry The recorded change.
 * @param rateTypeId The rate's type, which is where the unit comes from (`15` §5).
 */
export function valueTransition(entry: RateHistoryEntry, rateTypeId: number): string | null {
  const standard = transition(entry.previousValue, entry.newValue, rateTypeId);
  const temporary = transition(entry.previousTemporaryValue, entry.newTemporaryValue, rateTypeId);

  if (standard && temporary) return `${standard} · temporária ${temporary}`;
  if (temporary) return `temporária ${temporary}`;
  return standard;
}

/**
 * One pair of values as an arrow, or a single value when only one side exists — a creation has no before
 * and a deletion has no after, and writing "— → 3,03%" would be noise around the only number that matters.
 *
 * @param previous The value before.
 * @param next The value after.
 * @param rateTypeId The rate's type.
 */
function transition(previous: number | null, next: number | null, rateTypeId: number): string | null {
  if (previous === null && next === null) return null;
  if (previous === null) return formatRateValue(next!, rateTypeId);
  if (next === null) return `${formatRateValue(previous, rateTypeId)} → sem valor`;
  if (previous === next) return formatRateValue(next, rateTypeId);

  return `${formatRateValue(previous, rateTypeId)} → ${formatRateValue(next, rateTypeId)}`;
}

/**
 * An ISO instant as a date and time for reading.
 *
 * Formatted through the browser's own locale rather than by hand: unlike the window boundaries, which are
 * bare dates the backend sends as text, this is a real instant and the operator's clock is the right one to
 * read it in.
 *
 * @param iso The ISO 8601 instant.
 */
export function formatInstant(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;

  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
